import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/Layout/Layout";
import { MainHeading } from "@/components/common/MainHeading";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { IDiscount, ISingleDiscountResponse } from "@/interfaces/Discount/discount.interface";
import { formatRand, formatStatus } from "@/utility/helper";
import { StatusBadge } from "@/components/common/StatusBadge";
import CommonTable, { TableColumn } from "@/components/common/CommonTable";
import { usePermissions } from "@/hooks/usePermissions";
import { RootState, useAppSelector } from "@/redux/store";

export const ViewDiscount = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [discount, setDiscount] = useState<IDiscount | null>(null);
    const [loading, setLoading] = useState(true);
    const { canView, canDelete, canEdit } = usePermissions();
    const user = useAppSelector((state: RootState) => state?.user?.user);

    useEffect(() => {
        const fetchDiscount = async () => {
            try {
                const response = await callApi<ISingleDiscountResponse>(
                    `${API_URL + API_ENDPOINT.discounts}/${id}`,
                    { method: 'GET' }
                );
                setDiscount(response?.data);
            } catch (error) {
                ThrowError(error);
            } finally {
                setLoading(false);
            }
        };
        if (id) fetchDiscount();
    }, [id]);

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center min-h-[400px]">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                </div>
            </Layout>
        );
    }

    if (!discount) {
        return (
            <Layout>
                <MainHeading title="Discount Details" showBack onBack={() => navigate(-1)} />
                <div className="text-center py-10 text-muted-foreground">Discount not found.</div>
            </Layout>
        );
    }

    const valueDisplay = discount.discountType === 'percentage' ? `${discount.value}%` : formatRand(Number(discount.value));

    const productColumns: TableColumn<any>[] = [
        {
            key: "product",
            header: "Product",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-secondary/20 overflow-hidden flex-shrink-0 border">
                        {item.media?.[0] ? (
                            <img src={`${API_URL}/${item.media[0].url}`} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-[8px] text-muted-foreground">No Img</div>
                        )}
                    </div>
                    <span className="font-medium text-xs line-clamp-1">{item.name}</span>
                </div>
            )
        },
        {
            key: "price",
            header: "Price",
            render: (item) => (
                <div className="font-semibold text-primary">
                    {formatRand(item.price)}
                </div>
            )
        },
    ];

    const categoryColumns: TableColumn<any>[] = [
        {
            key: "category",
            header: "Category",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded bg-secondary/20 overflow-hidden flex-shrink-0 border">
                        {item.media?.url ? (
                            <img src={`${API_URL}/${item.media.url}`} alt={item.name} className="w-full h-full object-cover" />
                        ) : (
                            <div className="w-full h-full flex items-center justify-center text-[8px] text-muted-foreground">No Img</div>
                        )}
                    </div>
                    <span className="font-medium text-xs line-clamp-1">{item.name}</span>
                </div>
            )
        },
        {
            key: "description",
            header: "Description",
            render: (item) => <span className="text-xs text-muted-foreground line-clamp-1">{item.description || "N/A"}</span>
        },
    ];

    const targetData = discount.targetType === 'product' ? (discount.products ? discount.products : []) : (discount.category ? [discount.category] : []);

    return (
        <Layout>
            <MainHeading title="Discount Details" showBack onBack={() => navigate(-1)} />

            <div className="bg-card rounded-xl border p-8 shadow-sm mb-6 mt-6">
                <div className="border-b pb-6 mb-6 flex justify-between items-start">
                    <div>
                        <h2 className="text-2xl font-bold text-foreground">{discount.name}</h2>
                        {/* <p className="text-muted-foreground mt-1">Discount ID: {discount.id}</p> */}
                    </div>
                    <StatusBadge status={discount.status || 'inactive'} className="text-sm px-3 py-1" />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-y-8 gap-x-12">
                    <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1 uppercase tracking-wider">Discount Value</p>
                        <p className="font-bold text-primary text-3xl">{valueDisplay}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1 uppercase tracking-wider">Discount Type</p>
                        <p className="font-semibold text-lg capitalize">{discount.discountType}</p>
                    </div>
                    <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1 uppercase tracking-wider">Campaign Type</p>
                        <p className="font-semibold text-lg capitalize">{formatStatus(discount.promoTag) || '---'}</p>
                    </div>

                    <div>
                        <p className="text-sm font-medium text-muted-foreground mb-1 uppercase tracking-wider">Validity Period</p>
                        <div className="flex flex-col gap-1 text-base">
                            <div className="flex items-center gap-2">
                                <span className="font-semibold w-12 text-muted-foreground">Start:</span>
                                <span>{discount.startDate ? new Date(discount.startDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="font-semibold w-12 text-muted-foreground">End:</span>
                                <span>{discount.endDate ? new Date(discount.endDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : 'N/A'}</span>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-10 border-t pt-8">
                    <h3 className="text-lg font-bold text-foreground mb-4">Targeted Item <span className="text-sm font-normal text-muted-foreground capitalize ml-2">({discount.targetType})</span></h3>
                    <div className="border rounded-xl bg-secondary/5 shadow-inner overflow-hidden">
                        <CommonTable
                            columns={discount.targetType === 'product' ? productColumns : categoryColumns}
                            data={targetData}
                            loading={false}
                            className="border-none shadow-none"
                        />
                    </div>
                </div>
            </div>
        </Layout>
    );
};
